import { format, isToday, parse, type Locale } from "date-fns";
import { enUS, es } from "date-fns/locale";

export const localeMap: Record<string, Locale> = {
  "en-us": enUS,
  "es-us": es,
};

/**
 * Formats a date string to display translated "Today" for today's date or formatted date for other dates
 * @param dateString Date string in YYYY-MM-DD format
 * @param t Translation function
 * @param locale Date-fns locale object
 * @returns Formatted date string for display
 */
export const formatDateHeader = (
  dateString: string,
  t: (key: string) => string,
  locale: Locale,
): string => {
  const date = parse(dateString, "yyyy-MM-dd", new Date());

  if (isToday(date)) {
    return t("DateAndTime.today");
  }

  return format(date, "EEE, MMM d", {
    locale,
  });
};

/**
 * Format an ISO string from API (with offset) to a "h:mm am/pm" string,
 * without converting to the browser's local timezone.
 *
 * Example:
 *   formatApiTime("2025-08-25T15:00:00-04:00") -> "3:00 pm"
 */
export function formatApiTime(isoString: string | null | undefined): string {
  if (!isoString) return ""; // handles null, undefined, empty string
  // Match "T15:00:00-04:00" → groups: [hour, minute]
  const match = /T(\d{2}):(\d{2}):\d{2}/.exec(isoString);

  if (!match) return "";

  let hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);

  // Work out am/pm and convert to 12-hour clock
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;

  return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
}
